import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import geminiService from '../services/geminiService';
import { formatTime } from '../utils/dateUtils';
import config from '../config';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isTyping?: boolean;
  image?: {
    data: string;
    mimeType: string;
  };
}

interface SiteSettings {
  site_title: string;
  site_description: string;
  professional_title: string;
  tagline: string;
  years_experience: string;
  certification: string;
}

const ChatbotContainer = styled.div`
  position: fixed;
  bottom: 20px; /* Position at same level as back-to-top button */
  right: 20px;
  z-index: 1001;
  
  @media (max-width: 768px) {
    bottom: 15px; /* Same level as back-to-top button */
    right: 15px;
  }
  
  @media (max-width: 480px) {
    bottom: 15px;
    right: 10px;
  }
`;

const ChatInterface = styled(motion.div)`
  position: relative;
  width: 380px;
  height: 500px;
  background: ${props => props.theme.colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 10px 40px ${props => props.theme.colors.shadow};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: calc(100vw - 30px);
    max-width: 320px;
    height: 450px;
    bottom: 15px;
    right: 15px;
  }
  
  @media (max-width: 480px) {
    width: calc(100vw - 20px);
    max-width: 300px;
    height: 400px;
    bottom: 20px;
    right: 10px;
    /* Ensure chat doesn't get cut off */
    max-height: calc(100vh - 40px);
  }
`;

const ChatHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
  }
  
  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .status {
    font-size: 0.85rem;
    opacity: 0.95;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
    
    &::before {
      content: '';
      width: 8px;
      height: 8px;
      background: #4ade80;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
  }
  
  @media (max-width: 768px) {
    padding: 18px 20px;
    
    h3 {
      font-size: 1.1rem;
    }
    
    .status {
      font-size: 0.8rem;
    }
  }
`;

const ImageUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ImageUploadButton = styled.label`
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;
  flex-shrink: 0;
  aspect-ratio: 1;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
  
  input {
    display: none;
  }
  
  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
    min-width: 34px;
    min-height: 34px;
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    font-size: 0.9rem;
  }
`;

const ImagePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  margin-bottom: 8px;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    object-fit: cover;
  }
  
  .remove-btn {
    background: ${props => props.theme.colors.error || '#ff4444'};
    color: white;
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    cursor: pointer;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.surface};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
  }
`;

const MessageBubble = styled(motion.div)<{ sender: 'user' | 'bot' }>`
  max-width: 85%;
  padding: 14px 18px;
  border-radius: 20px;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  align-self: ${props => props.sender === 'user' ? 'flex-end' : 'flex-start'};
  
  ${props => props.sender === 'user' ? `
    background: linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primary}dd);
    color: white;
    border-bottom-right-radius: 8px;
    box-shadow: 0 2px 8px ${props.theme.colors.primary}33;
  ` : `
    background: ${props.theme.colors.surface};
    color: ${props.theme.colors.text};
    border-bottom-left-radius: 8px;
    border: 1px solid ${props.theme.colors.border};
    box-shadow: 0 1px 4px ${props.theme.colors.shadow};
  `}
  
  /* Enhanced text formatting */
  strong, b {
    font-weight: 600;
    color: ${props => props.sender === 'user' ? 'white' : props.theme.colors.primary};
  }
  
  /* Emoji styling */
  .emoji {
    font-size: 1.1em;
    margin: 0 2px;
  }
  
  /* Responsive design */
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-width: 90%;
    padding: 12px 16px;
    font-size: 0.9rem;
  }
  
  /* Link styling */
  a {
    color: ${props => props.sender === 'user' ? 'white' : props.theme.colors.primary};
    text-decoration: underline;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;



const InputContainer = styled.div`
  padding: 16px;
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  gap: 8px;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 14px 12px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 10px;
    gap: 6px;
    min-height: 60px;
    box-sizing: border-box;
  }
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  font-size: 0.9rem;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  outline: none;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SendButton = styled(motion.button)`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px ${props => props.theme.colors.shadow};
  flex-shrink: 0;
  padding: 0;
  
  /* Ensure perfect circle on all screen sizes */
  aspect-ratio: 1;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-1px) scale(1.05);
    box-shadow: 0 4px 12px ${props => props.theme.colors.shadow};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0) scale(0.95);
  }
  
  /* Compact sizing for small screens */
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    min-width: 38px;
    min-height: 38px;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    font-size: 0.95rem;
  }
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  align-self: flex-start;
  max-width: 80px;
  
  .dot {
    width: 6px;
    height: 6px;
    background: ${props => props.theme.colors.textSecondary};
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

const FloatingButton = styled(motion.button)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.theme.colors.gradient};
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px ${props => props.theme.colors.shadow};
  border: 2px solid ${props => props.theme.colors.border};
  
  &:hover {
    box-shadow: 0 6px 25px ${props => props.theme.colors.shadow};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
`;

const CloseButton = styled(motion.button)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 28px;
    height: 28px;
    font-size: 1rem;
  }
`;

const FloatingChatbot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<{data: string; mimeType: string} | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    site_title: '',
    site_description: '',
    professional_title: '',
    tagline: '',
    years_experience: '',
    certification: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const welcomeMessage: Message = useMemo(() => ({
    id: 'welcome',
    text: `Hello! I'm ${siteSettings.site_title || 'Dr. Ramhari Gurjar'}'s AI assistant. I can help you with poultry veterinary care, farm management, and health advice. How can I assist you today?`,
    sender: 'bot',
    timestamp: new Date()
  }), [siteSettings.site_title]);

  // Fetch site settings
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/public-site-settings.php`);
        if (response.data && Object.keys(response.data).length > 0) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };

    fetchSiteSettings();
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Clear any existing conversation history and initialize with welcome message
      geminiService.clearHistory();
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length, welcomeMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-hide when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && chatContainerRef.current && !chatContainerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessage = (text: string) => {
    // Convert **text** to bold
    let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *text* to bold (alternative syntax)
    formatted = formatted.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    
    // Enhance emoji rendering (compatible with older JS versions)
    formatted = formatted.replace(/([\uD83D][\uDE00-\uDE4F]|[\uD83C][\uDF00-\uDFFF]|[\uD83D][\uDC00-\uDDFF]|[\u2600-\u26FF]|[\u2700-\u27BF])/g, '<span class="emoji">$1</span>');
    
    // Convert URLs to links
    formatted = formatted.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
    
    return formatted;
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = function(e) {
      const result = e.target?.result as string;
      setUploadedImage({
        data: result.split(',')[1], // Remove data:image/...;base64, prefix
        mimeType: file.type
      });
    };
    reader.readAsDataURL(file);
  };

  const removeImagePreview = () => {
    setUploadedImage(null);
  };

  const handleSendMessage = async () => {
    let message = inputValue.trim();
    
    // Check if we have an image but no text - auto-add prompt
    if (uploadedImage && !message) {
      message = "What's in this picture?";
    }
    
    // Must have either message or image
    if (!message && !uploadedImage) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: message,
      sender: 'user',
      timestamp: new Date(),
      image: uploadedImage || undefined
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      // Prepare image data if present
      let imageData: { data: string; mimeType: string } | undefined;
      if (uploadedImage) {
        imageData = {
          data: uploadedImage.data,
          mimeType: uploadedImage.mimeType
        };
      }
      
      // Get AI response from Gemini service with image if present
      const aiResponse = await geminiService.sendMessage(message, imageData);
      
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I apologize, but I'm having trouble connecting right now. Please try again later or contact Dr. Gurjar directly.",
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      // Clear uploaded image after sending
      if (uploadedImage) {
        removeImagePreview();
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <ChatbotContainer ref={chatContainerRef}>
      <AnimatePresence>
        {isOpen && (
          <ChatInterface
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
        <ChatHeader>
          <div>
            <h3>🤖 {siteSettings.site_title || 'Dr. Gurjar'}'s AI Assistant</h3>
            <div className="status">Online • Ready to help</div>
          </div>
          <CloseButton
            onClick={toggleChat}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </CloseButton>
        </ChatHeader>

        <MessagesContainer>
          {messages.map((message) => (
            <div key={message.id}>
              {message.image && (
                <MessageBubble
                  sender={message.sender}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={`data:${message.image.mimeType};base64,${message.image.data}`}
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', marginBottom: '8px', display: 'block' }}
                    alt="User upload"
                  />
                  {message.text && <div dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }} />}
                </MessageBubble>
              )}
              {!message.image && (
                <MessageBubble
                  sender={message.sender}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }}
                />
              )}
            </div>
          ))}

          {isTyping && (
            <TypingIndicator
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </TypingIndicator>
          )}

          <div ref={messagesEndRef} />
        </MessagesContainer>

        {uploadedImage && (
          <ImagePreview>
            <img 
              src={`data:${uploadedImage.mimeType};base64,${uploadedImage.data}`}
              alt="Selected file"
            />
            <span>Image ready to send</span>
            <button className="remove-btn" onClick={removeImagePreview}>×</button>
          </ImagePreview>
        )}

        <InputContainer>
          <ImageUploadContainer>
            <ImageUploadButton>
              📷
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </ImageUploadButton>
          </ImageUploadContainer>
          <MessageInput
            type="text"
            placeholder="Message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <SendButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() && !uploadedImage}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ➤
          </SendButton>
        </InputContainer>
          </ChatInterface>
        )}
      </AnimatePresence>
      
      {!isOpen && (
        <FloatingButton
          onClick={toggleChat}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          💬
        </FloatingButton>
      )}
    </ChatbotContainer>
  );
};

export default FloatingChatbot;